import * as z from 'zod'

import {
  UserPreferences,
  userPreferencesSchema,
  userPreferencesBaseOps,
  getVariantsDataFromKey,
  TimeSeriesRange,
  rootCommentSchema,
} from '@basehub/data/replicache/index'
import type { WriteTransaction } from 'replicache'
import { makeBlocksFavorite, makeBlocksUnFavorite } from './fav'
import type { VariantData } from '@basehub/data/replicache/helpers/variants'

async function initUserPreferences(
  tx: WriteTransaction,
  userId: string
): Promise<UserPreferences> {
  const existingUserPreferences = await userPreferencesBaseOps.get(tx, userId)
  if (existingUserPreferences) {
    const parsedExistingUserPreferences = userPreferencesSchema.parse(
      existingUserPreferences
    )
    return parsedExistingUserPreferences
  }

  const newUserPreferences: UserPreferences = {
    id: userId,
    favoriteBlocks: [],
    propertiesPanelMode: 'properties',
    historyPanelFilters: {
      range: null,
      members: null,
    },
    collapsePropertiesPanel: false,
    collapseDocumentPanel: false,
    editMode: false,
    listSelectedView: {},
    eventSelectedRange: {},
    selectedThreads: {},
    commentsRead: {},
    collapsibleGroups: {},
    hideOnboardingGuides: false,
  }
  await userPreferencesBaseOps.put(tx, newUserPreferences)

  return newUserPreferences
}

async function toggleCollapsePropertiesPanel(
  tx: WriteTransaction,
  userId: string
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${userId} should exist`)
  }
  await userPreferencesBaseOps.update(tx, {
    id: userId,
    collapsePropertiesPanel: !userPreferences.collapsePropertiesPanel,
  })
}

async function setPropertiesPanelMode(
  tx: WriteTransaction,
  data: {
    userId: string
    mode: 'properties' | 'history'
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }
  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    propertiesPanelMode: data.mode,
  })
}

async function setCollapsedState(
  tx: WriteTransaction,
  data: {
    userId: string
    groupId: string
    isCollapsed: boolean
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  const newCollapsibleGroups = {
    ...userPreferences.collapsibleGroups,
    [data.groupId]: data.isCollapsed,
  }

  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    collapsibleGroups: newCollapsibleGroups,
  })
}

async function setCommitsFilters(
  tx: WriteTransaction,
  data: {
    filterType: 'commitsFilters' | 'historyPanelFilters'
    userId: string
    range?: { from: string; to: string } | null
    members?: string[] | null
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  const newFilters: UserPreferences['historyPanelFilters'] = {}
  if (typeof data.range !== 'undefined') {
    newFilters.range = data.range
  }
  if (typeof data.members !== 'undefined') {
    newFilters.members = data.members
  }

  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    [data.filterType]: {
      ...userPreferences[data.filterType],
      ...newFilters,
    },
  })
}

async function setCollapsedDocumentPanel(
  tx: WriteTransaction,
  data: {
    userId: string
    isCollapsed: boolean
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    collapseDocumentPanel: data.isCollapsed,
  })
}

async function setCollapsedPropertiesPanel(
  tx: WriteTransaction,
  data: {
    isCollapsed: boolean
    userId: string
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }
  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    collapsePropertiesPanel: data.isCollapsed,
  })
}

async function setEditMode(
  tx: WriteTransaction,
  data: {
    userId: string
    editMode: boolean
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }
  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    editMode: data.editMode,
  })
}

async function setSelectedListView(
  tx: WriteTransaction,
  data: {
    userId: string
    listBlockId: string
    listBlockViewId: string
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  const newSelectedView = {
    ...userPreferences.listSelectedView,
    [data.listBlockId]: data.listBlockViewId,
  }

  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    listSelectedView: newSelectedView,
  })
}

async function setSelectedThreadByBlock(
  tx: WriteTransaction,
  data: {
    userId: string
    blockId: string
    branchId: string
    rootComment: z.infer<typeof rootCommentSchema> | null
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  const { rootComment, blockId, branchId } = data

  const threadsOfThisBlock = userPreferences.selectedThreads[data.blockId]
  if (!threadsOfThisBlock || !rootComment) {
    const newThreads = { ...userPreferences.selectedThreads }

    if (rootComment) {
      newThreads[data.blockId] = [rootComment]
    } else {
      newThreads[data.blockId] = (newThreads[data.blockId] ?? []).filter(
        (rootComment) =>
          rootComment.branchId !== branchId || rootComment.blockId !== blockId
      )
    }

    await userPreferencesBaseOps.update(tx, {
      id: data.userId,
      selectedThreads: newThreads,
    })

    return
  }

  let override = false
  let selectedThreadsForThisBlock = [...threadsOfThisBlock]
  selectedThreadsForThisBlock = threadsOfThisBlock.map((_rootComment) => {
    if (_rootComment.branchId !== branchId) return _rootComment
    if (_rootComment.blockId !== blockId) return _rootComment
    if (_rootComment.id === rootComment.id) return _rootComment
    override = true
    return rootComment
  })

  if (!override) {
    selectedThreadsForThisBlock.push(rootComment)
  }

  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    selectedThreads: {
      ...userPreferences.selectedThreads,
      [data.blockId]: selectedThreadsForThisBlock,
    },
  })
}

async function setSelectedEventRange(
  tx: WriteTransaction,
  data: {
    userId: string
    eventBlockId: string
    rangeValue: TimeSeriesRange
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  const newSelectedRange = {
    ...userPreferences.eventSelectedRange,
    [data.eventBlockId]: data.rangeValue,
  }

  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    eventSelectedRange: newSelectedRange,
  })
}

async function hideOnboardingGuides(
  tx: WriteTransaction,
  data: { userId: string }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    hideOnboardingGuides: true,
  })
}

async function setCommentRead(
  tx: WriteTransaction,
  data: {
    userId: string
    commentId: string
    isoNow: string
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  const newCommentsRead = {
    ...userPreferences.commentsRead,
    [data.commentId]: data.isoNow,
  }

  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    commentsRead: newCommentsRead,
  })
}

async function setSelectedVariants(
  tx: WriteTransaction,
  data: {
    userId: string
    variants: VariantData[] | string
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    selectedVariants:
      typeof data.variants === 'string'
        ? getVariantsDataFromKey(data.variants)
        : data.variants,
  })

  return data.variants
}

async function toggleSelectedVariant(
  tx: WriteTransaction,
  data: {
    userId: string
    variant: VariantData
  }
) {
  const userPreferences = await userPreferencesBaseOps.get(tx, data.userId)
  if (!userPreferences) {
    throw new Error(`userPreferences for user ${data.userId} should exist`)
  }

  if (!userPreferences.selectedVariants) {
    userPreferences.selectedVariants = [data.variant]
  } else {
    const setMatch = userPreferences.selectedVariants.find(
      (selected) => selected.setId === data.variant.setId
    )
    if (setMatch && setMatch.variantId !== data.variant.variantId) {
      userPreferences.selectedVariants =
        userPreferences.selectedVariants.filter(
          (v) => v.setId !== data.variant.setId
        )
    }

    if (!setMatch || setMatch.variantId !== data.variant.variantId) {
      userPreferences.selectedVariants.push(data.variant)
    }
  }

  const newVariants = [...userPreferences.selectedVariants]
  await userPreferencesBaseOps.update(tx, {
    id: data.userId,
    selectedVariants: newVariants,
  })

  return newVariants
}

// exports

export const userPreferencesMutators = {
  // crud
  _getUserPreferences: userPreferencesBaseOps.get,
  _putUserPreferences: userPreferencesBaseOps.put,
  _updateUserPreferences: userPreferencesBaseOps.update,
  _deleteUserPreferences: userPreferencesBaseOps.delete,
  _hasUserPreferences: userPreferencesBaseOps.has,
  _listUserPreferences: userPreferencesBaseOps.list,
  // init
  initUserPreferences,
  // fav
  makeBlocksFavorite,
  makeBlocksUnFavorite,
  // prop panel
  toggleCollapsePropertiesPanel,
  setCollapsedPropertiesPanel,
  setPropertiesPanelMode,
  setCommitsFilters,
  setCollapsedState,
  // doc panel
  setCollapsedDocumentPanel,
  // edit mode
  setEditMode,
  // list view
  setSelectedListView,
  // event range
  setSelectedEventRange,
  // comments
  setCommentRead,
  setSelectedThreadByBlock,
  // timeline
  hideOnboardingGuides,
  // variants
  toggleSelectedVariant,
  setSelectedVariants,
}
